import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FolderTreeNodeStats, Model, Participant, Project } from '@bimdata/definitions/dto';
import { ProjectData } from './types/project-data';
import { ProjectContents } from './types/project-contents';
import { getUserLabel } from '@/contracts';

import { ProjectUpdateParams, RequestStatus } from '@/contracts';
import { AccessLevel, ModelStatus, UserStatus } from '@bimdata/definitions/const';
import { CompareSections } from './types/section';
import { Invitation } from './types/invitation';
import { ModelData } from './types/model-data';

export type ProjectsState = {
  current: ProjectData | null,
  openingStatus: RequestStatus,
  openingError: Error | null,
  listFetchStatus: RequestStatus,
  logos: Record<string, { status: RequestStatus, source: string | null }>,
  thumbnails: Record<string, { status: RequestStatus, source: string | null }>,
  list: Project[] | null,
  shareCodes: Record<string, string | null>
};

function toModelData(model: Model): ModelData {
  return {
    sourceSize: model.sourceSize,
    id: model.id,
    name: model.name,
    section: model.section,
    filename: model.filename,
    ready: [ModelStatus.READY, ModelStatus.ERROR].includes(model.status),
    failed: model.status == ModelStatus.ERROR,
    elements: model.misc?.elements || 0,
    geometries: model.misc?.geometries || 0,
    views: (model.misc?.info?.views && Array.isArray(model.misc?.info?.views))
      ? model.misc?.info?.views
      : [],
    loaded: model.loaded,
    revitVersion: model.misc?.info?.optional?.revitVersion,
    ...(typeof model.author === 'object'
      ? { author: model.author }
      : { author: { id: '', email: '', fullName: '', avatar: null, status: UserStatus.INACTIVE } }
    ),
  };
}

function joinModelDatas(
  { sourceSize, author, elements, geometries, ...main }: ModelData,
  { author: oAuthor, elements: oElements, geometries: oGeometries, ...old}: ModelData
): ModelData {
  return {
    sourceSize,
    ...old,
    author: !!author.id ? author : oAuthor,
    elements: elements || oElements || 0,
    geometries: geometries || oGeometries || 0,
    ...main,
  };
}

export default createSlice({
  name: 'project',
  initialState: {
    list: null,
    current: null,
    openingStatus: RequestStatus.IDLE,
    openingError: null,
    listFetchStatus: RequestStatus.IDLE,
    logos: {},
    thumbnails: {},
    shareCodes: {},
  } as ProjectsState,
  reducers: {
    reset: (state: ProjectsState) => {
      state.list = null;
      state.current = null;
      state.openingStatus = RequestStatus.IDLE;
      state.listFetchStatus = RequestStatus.IDLE;
      state.logos = {};
      state.thumbnails = {};
    },
    created: (state: ProjectsState, { payload }: PayloadAction<Project>) => {
      if (Array.isArray(state.list) && state.listFetchStatus !== RequestStatus.BUSY) {
        state.list = [
          ...state.list.filter(({ code }) => code !== payload.code),
          {
            ...payload,
            additionalSections: Array.from(payload.additionalSections).sort((a, b) => a.localeCompare(b))
          },
        ];
      }
    },
    listFetching: (state: ProjectsState, {}: PayloadAction<void>) => {
      state.listFetchStatus = RequestStatus.BUSY;
    },
    listFetched: (state: ProjectsState, { payload }: PayloadAction<Project[]>) => {
      state.list = payload;
      state.listFetchStatus = RequestStatus.IDLE;
    },
    update: (
      state: ProjectsState,
      { payload }: PayloadAction<{ code: string, data?: ProjectUpdateParams}>
    ) => {
      let { code, data } = payload;
      if (state.current?.code === code) {
        if (data) {
          Object.assign(state.current, data);
          state.current.additionalSections.sort((a, b) => a.localeCompare(b));
        }
      }
      if (Array.isArray(state.list) && state.listFetchStatus !== RequestStatus.BUSY) {
        const fetchedProject = state.list.find((p) => p.code === code);
        if (fetchedProject) {
          if (data) {
            Object.assign(fetchedProject, data);
            fetchedProject.additionalSections.sort((a, b) => a.localeCompare(b));
          }
          fetchedProject.updated = new Date();
        }
      }
    },
    open: (state: ProjectsState, {}: PayloadAction<void>) => {
      state.current = null;
      state.openingStatus = RequestStatus.BUSY;
    },
    opened: (state: ProjectsState, { payload }: PayloadAction<ProjectData>) => {
      state.current = { ...payload };
      state.current.additionalSections.sort((a, b) => a.localeCompare(b));
      state.current.models?.sort(CompareSections);
      state.openingStatus = RequestStatus.IDLE;
    },
    close: (state: ProjectsState, {}: PayloadAction<void>) => {
      state.current = null;
      state.openingStatus = RequestStatus.IDLE;
    },
    openFailed: (state: ProjectsState, { payload }: PayloadAction<Error | null>) => {
      state.current = null;
      state.openingStatus = RequestStatus.ERROR;
      state.openingError = payload;
    },
    updated: (state: ProjectsState, { payload }: PayloadAction<ProjectContents>) => {
      if (state.current && state.current.code == payload.code) {
        state.current.models = payload.models?.sort(CompareSections);
      }
    },
    classificationLoaded: (state: ProjectsState, { payload }: PayloadAction<FolderTreeNodeStats[]>) => {
      if (state.current) {
        state.current.classification = payload;
      }
    },
    logoBusy: (state: ProjectsState, { payload }: PayloadAction<string>) => {
      if (state.logos[payload]?.status !== RequestStatus.BUSY) {
        state.logos = {
          ...state.logos,
          [payload]: {
            status: RequestStatus.BUSY,
            source: null,
          }
        };
      }
    },
    logoIdle: (
      state: ProjectsState,
      { payload }: PayloadAction<{ projectId: string, source: string | null }>
    ) => {
      state.logos = {
        ...state.logos,
        [payload.projectId]: {
          status: RequestStatus.IDLE,
          source: payload.source,
        }
      };
    },
    logoError: (state: ProjectsState, { payload }: PayloadAction<string>) => {
      state.logos = {
        ...state.logos,
        [payload]: {
          status: RequestStatus.ERROR,
          source: null,
        }
      };
    },
    clearLogo: (state: ProjectsState, { payload }: PayloadAction<string>) => {
      delete state.logos[payload];
      state.logos = {
        ...state.logos
      };
    },
    thumbnailBusy: (state: ProjectsState, { payload }: PayloadAction<string>) => {
      if (state.thumbnails[payload]?.status !== RequestStatus.BUSY) {
        state.thumbnails = {
          ...state.thumbnails,
          [payload]: {
            status: RequestStatus.BUSY,
            source: null,
          }
        };
      }
    },
    thumbnailIdle: (
      state: ProjectsState,
      { payload }: PayloadAction<{ projectId: string, source: string | null }>
    ) => {
      state.thumbnails = {
        ...state.thumbnails,
        [payload.projectId]: {
          status: RequestStatus.IDLE,
          source: payload.source,
        }
      };
    },
    thumbnailError: (state: ProjectsState, { payload }: PayloadAction<string>) => {
      state.thumbnails = {
        ...state.thumbnails,
        [payload]: {
          status: RequestStatus.ERROR,
          source: null,
        }
      };
    },
    clearThumbnail: (state: ProjectsState, { payload }: PayloadAction<string>) => {
      delete state.thumbnails[payload];
      state.thumbnails = {
        ...state.thumbnails
      };
    },
    removeProject: (state: ProjectsState, { payload }: PayloadAction<string>) => {
      if (Array.isArray(state.list) && state.listFetchStatus !== RequestStatus.BUSY) {
        state.list = state.list.filter((p) => p.code !== payload);
        delete state.logos[payload];
        state.logos = {
          ...state.logos
        };
      }
    },
    addModels: (
      state: ProjectsState,
      { payload }: PayloadAction<{ code: string, models: Model[] }>
    ) => {
      if (state.current?.code === payload.code) {
        const writePlan = new Map<string | null, Set<number>>();
        //Данные для записи, далее они будут мутироваться при объединении со старой версией
        const writeData = new Map<number, ModelData>();
        for (const model of payload.models) {
          if (!writePlan.has(model.section)) {
            writePlan.set(model.section, new Set<number>());
          }
          writePlan.get(model.section)?.add(model.id);
          writeData.set(model.id, toModelData(model));
        }

        const enriched = (state.current.models || []).map((section) => {
          const replacements = section.models.reduce<ModelData[]>((acc, m) => {
            //Модель не изменена
            if (!writeData.has(m.id)) {
              acc.push(m);
              return acc;
            }

            //Модель участвует в изменениях, мутируем объект данных для записи
            const data = writeData.get(m.id)!;
            Object.assign(data, joinModelDatas(data, m));

            //Модель перенесена в другую секцию, в этой удаляется
            if (data.section !== m.section) {
              return acc;
            }

            //Тут мы уже можем быть уверены, что модель в плане записи в эту секцию
            //Удаляем модель из плана записи, добавляем мутобъект данных записи в текущую секцию
            writePlan.get(section.name)!.delete(m.id);
            acc.push(data);
            return acc;
          }, []);

          if (writePlan.has(section.name)) {
            for (const mid of writePlan.get(section.name)!) {
              replacements.push(writeData.get(mid)!);
            }
          }
          writePlan.delete(section.name);

          return {
            ...section,
            models: replacements.sort((a, b) => a.name.localeCompare(b.name)),
          };
        });

        state.current = {
          ...state.current,
          models: [
          ...enriched.filter((section) => section.models.length),
          ...Array.from(writePlan.entries()).map(([section, models]) => ({
              name: section,
              ready: true,
              elements: 0,
              geometries: 0,
              models: Array.from(models, (mid) => writeData.get(mid)!)
                .sort((a, b) => a.name.localeCompare(b.name)),
            })),
          ].sort(CompareSections)
            .map((section) => {
              return {
                ...section,
                ...(section.models.reduce<{ ready: boolean, elements: number, geometries: number }>(
                  (acc, m) => {
                    acc.ready &&= m.ready;
                    acc.elements += m.elements || 0,
                    acc.geometries += m.geometries || 0;
                    return acc;
                  },
                  {
                    ready: true,
                    elements: 0,
                    geometries: 0
                  }
                ))
              };
            })
        };
      }
    },
    removeModels: (state: ProjectsState, { payload }: PayloadAction<{ code: string, modelIds: number[] }>) => {
      const criteria = new Set(payload.modelIds);
      if (state.current?.code === payload.code) {
        state.current = {
          ...state.current,
          models: state.current.models?.map((section) => ({
            ...section,
            models: section.models.filter((model) => !criteria.has(model.id))
          })).filter((section) => section.models.length)
        };
      }
    },
    setInvitations: (state: ProjectsState, { payload }: PayloadAction<{ code: string, invitations: Invitation[] }>) => {
      if (state.current?.code === payload.code) {
        state.current = {
          ...state.current,
          invitations: payload.invitations,
        };
      }
    },
    setInvitationsAccessLevel: (state: ProjectsState, { payload }: PayloadAction<{ code: string, ids: string[], access: AccessLevel }>) => {
      if (state.current?.code === payload.code && state.current.invitations) {
        for (const id of payload.ids) {
          const invitation = state.current.invitations.find((i) => i.id === id);
          if (invitation) {
            Object.assign(invitation, {
              access: payload.access
            });
          }
        }
      }
    },
    addInvitation: (state: ProjectsState, { payload }: PayloadAction<{ code: string, invitation: Invitation }>) => {
      if (state.current?.code === payload.code && state.current.invitations) {
        state.current = {
          ...state.current,
          invitations: [
            ...state.current.invitations.filter(({ id }) => id !== payload.invitation.id),
            payload.invitation,
          ]
        };
      }
    },
    removeInvitations: (state: ProjectsState, { payload }: PayloadAction<{ code: string, ids: string[] }>) => {
      if (state.current?.code === payload.code && state.current.invitations) {
        const idSet = new Set(payload.ids);
        state.current = {
          ...state.current,
          invitations: state.current.invitations.filter(({ id }) => !idSet.has(id)),
        };
      }
    },
    setParticipantsBlocked: (state: ProjectsState, { payload }: PayloadAction<{ code: string, ids: string[], blocked: boolean }>) => {
      if (state.current?.code === payload.code && state.current.participants) {
        for (const uid of payload.ids) {
          const participant = state.current.participants.find(({ user }) => user.id === uid);
          if (participant) {
            Object.assign(participant, {
              blocked: payload.blocked
            });
          }
        }
      }
    },
    setParticipantsAccessLevel: (state: ProjectsState, { payload }: PayloadAction<{ code: string, ids: string[], access: AccessLevel }>) => {
      if (state.current?.code === payload.code && state.current.participants) {
        for (const uid of payload.ids) {
          const participant = state.current.participants.find(({ user }) => user.id === uid);
          if (participant) {
            Object.assign(participant, {
              access: payload.access
            });
          }
        }
      }
    },
    setParticipants: (state: ProjectsState, { payload }: PayloadAction<{ code: string, participants: Participant[] }>) => {
      if (state.current?.code === payload.code) {
        state.current = {
          ...state.current,
          participants: payload.participants.sort((a, b) => getUserLabel(a.user).localeCompare(getUserLabel(b.user))),
        };
      }
    },
    removeParticipants: (state: ProjectsState, { payload }: PayloadAction<{ code: string, ids: string[] }>) => {
      if (state.current?.code === payload.code && state.current.participants) {
        const idSet = new Set(payload.ids);
        state.current = {
          ...state.current,
          participants: state.current.participants.filter(({ user }) => !idSet.has(user.id)),
        };
      }
    },
    setAccess: (state: ProjectsState, { payload }: PayloadAction<{ code: string, access: AccessLevel }>) => {
      if (state.current?.code === payload.code) {
        Object.assign(state.current, {
          access: payload.access,
          ...(
            (payload.access === AccessLevel.WRITER || payload.access === AccessLevel.READER)
            ? {
              invitations: null,
              participants: null
            }
            : {}
          )
        });
      }
      if (Array.isArray(state.list) && state.listFetchStatus !== RequestStatus.BUSY) {
        const fetchedProject = state.list.find((p) => p.code === payload.code);
        if (fetchedProject) {
          Object.assign(fetchedProject, {
            access: payload.access
          });
        }
      }
    },
    setShareCode: (state: ProjectsState, { payload }: PayloadAction<{ project: string, code: string | null }>) => {
      state.shareCodes[payload.project] = payload.code;
    },
  },
});
